import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import ReactPlayer from 'react-player';
import { timeFormatter } from '@act/shared/utilities';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { filesRequests } from 'redux/requests/files.requests';
import { TemporaryLinkResponse } from '@act/shared/data-transfer-objects';
import { DateTime } from 'luxon';
import {
  AUDIO_PLAYBACK_SIGNED_URL_TTL_SECONDS,
  isPlayableAudioMimeType,
  resolvePlaybackSource,
  signedPlaybackUrlRefreshDelayMs,
} from './audioPlayerPlayback.util';

interface AudioProgressState {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
}

interface PlayerState {
  tempFile?: TemporaryLinkResponse;
  url: string | null;
  urlExpiresAtMs: number | null;
  setup: boolean;
  error: boolean;
  mediaFailed: boolean;
  ready: boolean;
  playing: boolean;
  timePlayed: string;
  timeRemaining: string;
  played: number;
  playedSeconds: number;
  loaded: number;
  duration: number;
  seeking: boolean;
  playbackRate?: number;
}

export interface AudioPlayerController {
  disabled: boolean;
  playing: boolean;
  played: number;
  showPlayer: boolean;
  playerUrl: string | null;
  timePlayed: string;
  timeRemaining: string;
  downloadUrl: string | null;
  playerRef: RefObject<ReactPlayer>;
  onControlMouseDown: () => void;
  onTogglePlay: () => void;
  onSeekMouseDown: () => void;
  onSeekChange: (event: Event, newValue: number | number[]) => void;
  onSeekMouseUp: () => void;
  onReady: () => void;
  onPlay: () => void;
  onPause: () => void;
  onProgress: (state: AudioProgressState) => void;
  onDuration: (duration: number) => void;
  onError: () => void;
  onPlaybackRateChange: (speed: number | string) => void;
}

const MAX_SOURCE_RECOVERY_ATTEMPTS = 3;

const signedUrlParams = (id: string) => ({
  id,
  ttlSeconds: AUDIO_PLAYBACK_SIGNED_URL_TTL_SECONDS,
});

/**
 * Loads a signed audio URL and keeps the playhead across link refreshes.
 *
 * Requests a one-hour signed link and prefetches a replacement before it
 * expires. The media element keeps its current source until that source is
 * expired or has failed, then seeks back to the previous position.
 *
 * @param fileId Stored file id used to request a signed link.
 * @param tempFile Optional link already fetched by the caller.
 * @param mimeType File MIME type, when known.
 * @returns Player state and media-element event handlers.
 */
export function useAudioPlayer(
  fileId: string | undefined,
  tempFile: TemporaryLinkResponse | undefined,
  mimeType: string | undefined
): AudioPlayerController {
  const [signedUrl, _loadingSignedUrl, getSignedUrl] = useApiCall(
    filesRequests.getSignedUrl
  );

  const [playerState, setPlayerState] = useState<PlayerState>({
    tempFile,
    url: null,
    urlExpiresAtMs: null,
    setup: false,
    error: false,
    mediaFailed: false,
    ready: false,
    playing: false,
    timePlayed: '--:--',
    timeRemaining: '--:--',
    played: 0,
    playedSeconds: 0,
    loaded: 0,
    duration: 0,
    seeking: false,
  });

  const playerRef = useRef<ReactPlayer>(null);
  const playbackRef = useRef(playerState);
  playbackRef.current = playerState;
  const latestSignedUrlRef = useRef<TemporaryLinkResponse | null>(
    tempFile ?? null
  );
  const resumeSecondsRef = useRef<number | null>(null);
  const playWhenReadyRef = useRef(false);
  const recoveryAttemptsRef = useRef(0);
  const expirationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const getSignedUrlRef = useRef(getSignedUrl);
  getSignedUrlRef.current = getSignedUrl;

  const { setup, playing, played, error, duration, ready } = playerState;
  const disabled = error || !isPlayableAudioMimeType(mimeType);

  const scheduleSignedUrlRefresh = useCallback(
    (expiresAtMs: number, id: string) => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }
      const delay = signedPlaybackUrlRefreshDelayMs(
        expiresAtMs,
        DateTime.now().toMillis()
      );
      expirationTimeoutRef.current = setTimeout(() => {
        getSignedUrlRef.current({ params: signedUrlParams(id) });
      }, delay);
    },
    []
  );

  const applySignedLink = useCallback(
    (link: TemporaryLinkResponse) => {
      latestSignedUrlRef.current = link;
      const current = playbackRef.current;
      const decision = resolvePlaybackSource({
        activeUrl: current.url,
        activeExpiresAtMs: current.urlExpiresAtMs,
        playedSeconds: current.playedSeconds,
        playing: current.playing,
        nextUrl: link.temporaryUrl,
        nextExpiresAtMs: link.expires,
        nowMs: DateTime.now().toMillis(),
        mediaFailed: current.mediaFailed,
      });

      if (decision.resumeSeconds != null && decision.resumeSeconds > 0) {
        resumeSecondsRef.current = decision.resumeSeconds;
      }

      const starting =
        decision.resumeSeconds == null &&
        !current.playing &&
        current.playedSeconds <= 0;
      const durationLabel = link.file?.contentDuration
        ? timeFormatter.millisToDurationString(link.file.contentDuration)
        : '--:--';
      const shouldPlay = playWhenReadyRef.current;
      if (shouldPlay) {
        playWhenReadyRef.current = false;
      }

      setPlayerState(prev => ({
        ...prev,
        mediaFailed: false,
        tempFile: link,
        url: decision.url,
        urlExpiresAtMs:
          decision.url === prev.url && prev.urlExpiresAtMs != null
            ? prev.urlExpiresAtMs
            : link.expires,
        playing: shouldPlay ? true : prev.playing,
        ...(starting
          ? {
              timePlayed: link.file?.contentDuration ? '00:00' : '--:--',
              timeRemaining: durationLabel,
            }
          : {}),
      }));

      const id = link.file?.id || fileId;
      if (id) {
        scheduleSignedUrlRefresh(link.expires, id);
      }
    },
    [fileId, scheduleSignedUrlRefresh]
  );

  useEffect(() => {
    return () => {
      if (expirationTimeoutRef.current) {
        clearTimeout(expirationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    recoveryAttemptsRef.current = 0;
    if (fileId) {
      getSignedUrl({ params: signedUrlParams(fileId) });
    }
  }, [fileId, getSignedUrl]);

  useEffect(() => {
    if (!tempFile) {
      return;
    }
    applySignedLink(tempFile);
  }, [tempFile, applySignedLink]);

  useEffect(() => {
    if (!signedUrl) {
      return;
    }
    applySignedLink(signedUrl);
  }, [signedUrl, applySignedLink]);

  useEffect(() => {
    if (!played) {
      return;
    }

    const timePlayed = timeFormatter.millisToDurationString(
      played * duration * 1000
    );
    const timeRemaining = timeFormatter.millisToDurationString(
      (1 - played) * duration * 1000
    );

    setPlayerState(current => ({
      ...current,
      timePlayed,
      timeRemaining: `-${timeRemaining}`,
    }));
  }, [played, duration, ready]);

  const onPlay = () => {
    const current = playbackRef.current;
    const nowMs = DateTime.now().toMillis();
    if (current.urlExpiresAtMs != null && current.urlExpiresAtMs < nowMs) {
      const id = current.tempFile?.file?.id || fileId;
      if (id) {
        playWhenReadyRef.current = true;
        getSignedUrl({ params: signedUrlParams(id) });
        return;
      }
    }
    setPlayerState(currentState => ({ ...currentState, playing: true }));
  };

  const onPause = () => {
    setPlayerState(current => ({ ...current, playing: false }));
  };

  const onSeekMouseDown = () => {
    setPlayerState(current => ({ ...current, seeking: true }));
  };

  const onSeekChange = (_event: Event, newValue: number | number[]) => {
    const nextPlayed = Array.isArray(newValue) ? newValue[0] : newValue;
    setPlayerState(current => ({ ...current, played: nextPlayed }));
  };

  const onSeekMouseUp = () => {
    setPlayerState(current => ({ ...current, seeking: false }));
    playerRef.current?.seekTo(playbackRef.current.played);
  };

  const onProgress = (state: AudioProgressState) => {
    setPlayerState(current => {
      if (current.seeking) {
        return current;
      }
      return {
        ...current,
        played: state.played,
        playedSeconds: state.playedSeconds,
        loaded: state.loaded,
      };
    });
  };

  const onDuration = (nextDuration: number) => {
    setPlayerState(current => {
      const timeRemaining = timeFormatter.millisToDurationString(
        (1 - current.played) * nextDuration * 1000
      );
      return {
        ...current,
        duration: nextDuration,
        timeRemaining: `-${timeRemaining}`,
      };
    });
  };

  const onTogglePlay = () => {
    if (playing) {
      onPause();
      return;
    }
    onPlay();
  };

  const onControlMouseDown = () => {
    setPlayerState(current =>
      current.setup ? current : { ...current, setup: true }
    );
  };

  const onError = () => {
    const latest = latestSignedUrlRef.current;
    const current = playbackRef.current;
    const nowMs = DateTime.now().toMillis();
    const hasProgress = current.playing || current.playedSeconds > 0;
    const freshLink =
      latest != null &&
      latest.temporaryUrl !== current.url &&
      latest.expires > nowMs;
    const canRecover =
      hasProgress &&
      recoveryAttemptsRef.current < MAX_SOURCE_RECOVERY_ATTEMPTS;

    if (!canRecover) {
      setPlayerState(prev => ({ ...prev, error: true }));
      return;
    }

    recoveryAttemptsRef.current += 1;

    if (freshLink && latest) {
      resumeSecondsRef.current = current.playedSeconds;
      setPlayerState(prev => ({
        ...prev,
        error: false,
        mediaFailed: false,
        url: latest.temporaryUrl,
        urlExpiresAtMs: latest.expires,
      }));
      return;
    }

    const id = latest?.file.id || fileId;
    if (!id) {
      setPlayerState(prev => ({ ...prev, error: true }));
      return;
    }

    setPlayerState(prev => ({ ...prev, mediaFailed: true, error: false }));
    getSignedUrl({ params: signedUrlParams(id) });
  };

  const onReady = () => {
    const resumeSeconds = resumeSecondsRef.current;
    if (resumeSeconds != null && resumeSeconds > 0 && playerRef.current) {
      playerRef.current.seekTo(resumeSeconds, 'seconds');
      resumeSecondsRef.current = null;
    }
    setPlayerState(current => ({ ...current, ready: true }));
  };

  const onPlaybackRateChange = (speed: number | string) => {
    const playbackRate =
      typeof speed === 'number' ? speed : Number.parseFloat(speed);
    if (!Number.isFinite(playbackRate)) {
      return;
    }
    setPlayerState(current => ({ ...current, playbackRate }));
  };

  return {
    disabled,
    playing,
    played,
    showPlayer: setup && !disabled && !!playerState.url,
    playerUrl: playerState.url,
    timePlayed: playerState.timePlayed,
    timeRemaining: playerState.timeRemaining,
    downloadUrl: signedUrl?.temporaryUrl || playerState.url,
    playerRef,
    onControlMouseDown,
    onTogglePlay,
    onSeekMouseDown,
    onSeekChange,
    onSeekMouseUp,
    onReady,
    onPlay,
    onPause,
    onProgress,
    onDuration,
    onError,
    onPlaybackRateChange,
  };
}
